import React, { FC, KeyboardEvent, useState } from 'react'
import { Box, IconButton, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'

import { NotionTextField } from '@/apps/latest/components/portal/notion'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { PORTAL_TEXT } from '@/apps/latest/shared/styles/portalTypography'
import { RADIUS } from '@/shared/styles/radii'

interface Props {
  keywords: string[]
  onChange: (keywords: string[]) => void
  placeholder?: string
  maxKeywords?: number
  maxLength?: number
}

// キーワードのタグ入力 (latest 版)。Enter / カンマ / 読点で追加。
// チップは無彩色 (bg.muted)、常時カウンタ・ヘルプアイコンは持たない。
// エラーは上限・重複時のみ inline 表示する。
export const KeywordInput: FC<Props> = ({
  keywords,
  onChange,
  placeholder = 'キーワードを入力してEnterまたはカンマで追加',
  maxKeywords = 10,
  maxLength = 100,
}) => {
  const [inputValue, setInputValue] = useState('')
  const [error, setError] = useState('')

  const addKeyword = (raw: string) => {
    const trimmed = raw.trim()
    if (!trimmed) return
    if (trimmed.length > maxLength) {
      setError(`キーワードは${maxLength}文字以内で入力してください`)
      return
    }
    if (keywords.includes(trimmed)) {
      setError('同じキーワードが既に追加されています')
      return
    }
    if (keywords.length >= maxKeywords) {
      setError(`キーワードは最大${maxKeywords}個まで追加できます`)
      return
    }
    onChange([...keywords, trimmed])
    setInputValue('')
    setError('')
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    // IME 変換中の Enter (変換確定) では追加しない
    if (e.nativeEvent.isComposing) return
    if (e.key === 'Enter' || e.key === ',' || e.key === '、') {
      e.preventDefault()
      addKeyword(inputValue)
    }
  }

  return (
    <Box>
      <NotionTextField
        value={inputValue}
        onChange={(e) => {
          setInputValue(e.target.value)
          setError('')
        }}
        onKeyDown={handleKeyDown}
        onBlur={() => addKeyword(inputValue)}
        placeholder={placeholder}
        fullWidth
      />
      {error && (
        <Typography sx={{ ...PORTAL_TEXT.errorInline, mt: 0.5 }}>
          {error}
        </Typography>
      )}
      {keywords.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
          {keywords.map((keyword) => (
            <Box
              key={keyword}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.25,
                backgroundColor: colorTokens.bg.muted,
                color: colorTokens.fg.default,
                borderRadius: `${RADIUS.md}px`,
                pl: 1,
                pr: 0.25,
                py: 0.25,
              }}
            >
              <Typography component="span" sx={PORTAL_TEXT.badge}>
                {keyword}
              </Typography>
              <IconButton
                size="small"
                onClick={() => onChange(keywords.filter((k) => k !== keyword))}
                aria-label={`${keyword} を削除`}
                sx={{ p: 0.25 }}
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
}

export default KeywordInput
